import { useCallback, useEffect, useRef, useState } from "react";
import JMuxer from "../../Liveview/lib/jmuxer";
import { extractPlaybackStreamInfo } from "../lib/createPlaybackStreamCommand";

// Orca portal reference stack: codec.js + multipart.js + playback.js (media path)
const { G711AudioSink } = require("../lib/codec");
const {
  PlaybackVideoMuxer,
  PlaybackVideoClock,
  playMultipartResponse,
} = require("../lib/playbackMedia");

const PLAYBACK_DEBUG = false;
const dbg = (...args) => {
  if (PLAYBACK_DEBUG) console.log("[PlaybackDebug]", ...args);
};

const STALL_POLL_MS = 500;
const STALL_REPORT_MS = 2000;
const STALL_SUMMARY_MS = 2000;
// Reconnect after the server drops the stream mid-way: play out what arrived,
// then resume from the last presented frame on a fresh stream URL.
const RECONNECT_DRAIN_POLL_MS = 250;
const RECONNECT_DRAIN_LEAD_SECONDS = 0.3;
const RECONNECT_DRAIN_MAX_MS = 20000;
const RECONNECT_MIN_PROGRESS_MS = 2000;
const RECONNECT_MAX_ATTEMPTS = 3;
// Closer than this to endTime counts as a normal end, not an early close.
const RECONNECT_END_MARGIN_SECONDS = 3;

function boundedStatus(message) {
  return String(message).replace(/[\r\n]+/g, " ").slice(0, 256);
}

/** Resolves once the element has (nearly) played everything buffered. */
function waitForPlayoutDrain(videoEl, { isCurrent, isUserPaused }) {
  return new Promise((resolve) => {
    let playingMs = 0;
    const timer = window.setInterval(() => {
      if (!isCurrent()) {
        window.clearInterval(timer);
        resolve(false);
        return;
      }
      if (isUserPaused()) return;
      playingMs += RECONNECT_DRAIN_POLL_MS;
      const ranges = videoEl.buffered;
      const lead = ranges?.length ? ranges.end(ranges.length - 1) - videoEl.currentTime : 0;
      if (lead <= RECONNECT_DRAIN_LEAD_SECONDS || videoEl.ended || playingMs >= RECONNECT_DRAIN_MAX_MS) {
        window.clearInterval(timer);
        resolve(true);
      }
    }, RECONNECT_DRAIN_POLL_MS);
  });
}

/**
 * Counts buffer-edge events instead of logging each one: a source that cannot
 * keep up produces dozens of waiting/playing pairs per second, which is exactly
 * the micro-stutter, and per-event logging drowns everything else out.
 */
function attachVideoStallCounter(videoEl) {
  const counter = { waits: 0, stalledMs: 0 };
  if (!videoEl) return { counter, detach: () => {} };
  let waitingSince = null;
  const onWaiting = () => {
    counter.waits += 1;
    waitingSince = performance.now();
  };
  const onResume = () => {
    if (waitingSince === null) return;
    counter.stalledMs += performance.now() - waitingSince;
    waitingSince = null;
  };
  const onError = () => {
    dbg("video error", {
      code: videoEl.error?.code,
      message: videoEl.error?.message,
      currentTime: videoEl.currentTime,
    });
  };
  videoEl.addEventListener("waiting", onWaiting);
  videoEl.addEventListener("playing", onResume);
  videoEl.addEventListener("error", onError);
  return {
    counter,
    detach: () => {
      videoEl.removeEventListener("waiting", onWaiting);
      videoEl.removeEventListener("playing", onResume);
      videoEl.removeEventListener("error", onError);
    },
  };
}

/**
 * Playback stream session — orca/portal multipart path (not live viewer WS).
 *
 * Flow:
 *   1. createPlaybackStream → { protocol: multipart-replace, streamUrl, sessionId }
 *   2. fetch(streamUrl) once
 *   3. parse multipart → JMuxer video + G711 audio
 *   4. start() always aborts the previous fetch/session first
 */
export function usePlaybackStream({
  videoRef,
  deviceId,
  deviceSiteId,
  accessToken,
  streamId = 1,
  requestCreatePlaybackStream,
  enabled = true,
  muted = true,
  // Media-clock sink: called once per presented frame with seek+mediaTime (ms).
  // When the picture stalls this stops firing and the timeline freezes.
  onPlayheadPts = null,
  // Timeline UI's currently displayed time (ms) — for Pause Check vs absolute PTS.
  getTimelineUiMs = null,
}) {
  const [status, setStatusRaw] = useState("idle");
  // Seek/buffer: timeline must stay frozen until the first frame is painted.
  const [awaitingPicture, setAwaitingPicture] = useState(false);
  const generationRef = useRef(0);
  const abortRef = useRef(null);
  const muxerRef = useRef(null);
  const audioSinkRef = useRef(null);
  const activeRangeRef = useRef(null);
  const mutedRef = useRef(muted);
  const userPausedRef = useRef(false);
  const streamEndedRef = useRef(false);
  const startRef = useRef(null);
  const reconnectRef = useRef({ lastResumeMs: null, attempts: 0 });
  const onPlayheadPtsRef = useRef(onPlayheadPts);
  const getTimelineUiMsRef = useRef(getTimelineUiMs);
  const detachVideoDebugRef = useRef(null);
  // Unix seconds of the first frame in the active stream — lets callers map
  // media currentTime back to recording wall-clock time.
  const activeStartSecRef = useRef(null);
  const videoClockRef = useRef(null);
  const stallWatchRef = useRef(null);
  const pictureReadyRef = useRef(false);
  const cancelFramePumpRef = useRef(null);
  // Last composed frame (RVFC). Timeline reads this; stalls freeze it.
  const presentedFrameRef = useRef({ mediaTime: null, ptsEpochMs: null });

  useEffect(() => {
    onPlayheadPtsRef.current = onPlayheadPts;
  }, [onPlayheadPts]);

  useEffect(() => {
    getTimelineUiMsRef.current = getTimelineUiMs;
  }, [getTimelineUiMs]);

  const logPauseCheck = useCallback((reason) => {
    if (!PLAYBACK_DEBUG) return;
    const videoEl = videoRef.current;
    const presented = presentedFrameRef.current;
    const absolutePtsMs = Number.isFinite(presented?.ptsEpochMs)
      ? presented.ptsEpochMs
      : null;
    const timelineUiMs = getTimelineUiMsRef.current?.() ?? null;
    const deltaMs = Number.isFinite(absolutePtsMs) && Number.isFinite(timelineUiMs)
      ? Math.round(absolutePtsMs - timelineUiMs)
      : null;
    dbg("[Pause Check]", {
      t: Number(performance.now().toFixed(1)),
      reason,
      currentTime: videoEl && Number.isFinite(videoEl.currentTime)
        ? Number(videoEl.currentTime.toFixed(3))
        : null,
      absolutePtsIso: Number.isFinite(absolutePtsMs)
        ? new Date(absolutePtsMs).toISOString()
        : null,
      timelineUiIso: Number.isFinite(timelineUiMs)
        ? new Date(timelineUiMs).toISOString()
        : null,
      deltaMs,
      // deltaMs ≈ 0 → UI mirrors media clock. Fixed ~1000 → burn-in OSD skew
      // is separate from UI (compare absolutePtsIso to the picture OSD).
      mediaTime: presented?.mediaTime ?? null,
      readyState: videoEl?.readyState ?? null,
      paused: videoEl?.paused ?? null,
    });
  }, [videoRef]);

  const stopStallWatchdog = useCallback(() => {
    if (stallWatchRef.current !== null) {
      window.clearInterval(stallWatchRef.current);
      stallWatchRef.current = null;
    }
  }, []);

  const startStallWatchdog = useCallback(
    (generation, counter) => {
      stopStallWatchdog();
      if (!PLAYBACK_DEBUG) return;
      let lastTime = null;
      let frozenMs = 0;
      let reportedAt = 0;
      let windowAt = performance.now();
      let windowWaits = 0;
      let windowStalledMs = 0;
      stallWatchRef.current = window.setInterval(() => {
        const videoEl = videoRef.current;
        if (!videoEl || generation !== generationRef.current) return;
        const currentTime = videoEl.currentTime;
        const now = performance.now();

        // Micro-stutter: how much of the last window went to buffer-edge waits.
        if (now - windowAt >= STALL_SUMMARY_MS) {
          const waits = counter.waits - windowWaits;
          const stalledMs = counter.stalledMs - windowStalledMs;
          windowAt = now;
          windowWaits = counter.waits;
          windowStalledMs = counter.stalledMs;
          if (waits > 0) {
            dbg("video stalls", {
              waits,
              stalledMs: Math.round(stalledMs),
              currentTime: Number(currentTime.toFixed(2)),
              playbackRate: Number(videoEl.playbackRate.toFixed(2)),
              readyState: videoEl.readyState,
            });
          }
        }

        // Fully frozen picture while playing, a different problem from stutter.
        // A paused element is either buffering on purpose or the user's choice.
        if (!videoEl.paused && lastTime !== null && currentTime === lastTime) {
          frozenMs += STALL_POLL_MS;
        } else {
          frozenMs = 0;
          reportedAt = 0;
        }
        lastTime = currentTime;
        if (frozenMs < STALL_REPORT_MS || frozenMs === reportedAt) return;
        reportedAt = frozenMs;
        const audioPosition = audioSinkRef.current?.playbackPosition?.();
        dbg("video frozen", {
          frozenMs,
          currentTime,
          paused: videoEl.paused,
          playbackRate: videoEl.playbackRate,
          readyState: videoEl.readyState,
          audio: audioPosition
            ? { queued: audioPosition.queuedSeconds, playing: audioPosition.playing, held: audioPosition.held }
            : null,
          recordingIso:
            activeStartSecRef.current === null
              ? null
              : new Date(
                  videoClockRef.current?.epochMsAt(currentTime)
                    ?? (activeStartSecRef.current + currentTime) * 1000
                ).toISOString(),
        });
      }, STALL_POLL_MS);
    },
    [videoRef, stopStallWatchdog]
  );

  useEffect(() => {
    mutedRef.current = muted;
    if (videoRef.current) videoRef.current.muted = muted;
    const audioSink = audioSinkRef.current;
    if (!audioSink) return;
    if (muted) {
      // Drop what the worklet still holds so muting takes effect immediately.
      audioSink.resetPlayback?.();
      return;
    }
    // Toggling sound on is the user gesture the autoplay policy asks for, but
    // resume() can still stay pending, so never block on it.
    Promise.resolve(audioSink.unlock?.()).catch((err) => {
      dbg("audio unlock failed", { message: err?.message });
    });
  }, [muted, videoRef]);

  const setStatus = useCallback((msg) => setStatusRaw(boundedStatus(msg)), []);

  const teardown = useCallback(() => {
    stopStallWatchdog();
    detachVideoDebugRef.current?.();
    detachVideoDebugRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
    muxerRef.current?.destroy?.();
    muxerRef.current = null;
    audioSinkRef.current?.close?.();
    audioSinkRef.current = null;
    activeRangeRef.current = null;
    activeStartSecRef.current = null;
    videoClockRef.current = null;
    pictureReadyRef.current = false;
    presentedFrameRef.current = { mediaTime: null, ptsEpochMs: null };
    cancelFramePumpRef.current?.();
    cancelFramePumpRef.current = null;
    setAwaitingPicture(false);

    const videoEl = videoRef.current;
    if (videoEl) {
      try {
        videoEl.pause();
        videoEl.removeAttribute("src");
        videoEl.load();
      } catch {
        // ignore media reset errors
      }
    }
  }, [videoRef, stopStallWatchdog]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      teardown();
    },
    [deviceId, accessToken, streamId, teardown]
  );

  /**
   * Absolute recording PTS (ms) of the frame currently on screen.
   * Driven by requestVideoFrameCallback — freezes when the picture stalls.
   */
  const getMediaTimestampMs = useCallback(() => {
    if (!pictureReadyRef.current) return null;
    const presented = presentedFrameRef.current?.ptsEpochMs;
    if (Number.isFinite(presented)) return presented;
    // Fallback for browsers without RVFC: map currentTime → stored frame PTS.
    const startSec = activeStartSecRef.current;
    const videoEl = videoRef.current;
    if (startSec === null || !videoEl || videoEl.ended) return null;
    if (videoEl.paused && videoEl.readyState < 2) return null;
    const currentTime = videoEl.currentTime;
    if (!Number.isFinite(currentTime)) return null;
    const clock = videoClockRef.current;
    const buffered = videoEl.buffered;
    if (buffered?.length) clock?.calibrate(buffered.end(buffered.length - 1));
    const framePts = clock?.ptsEpochMsAt?.(currentTime) ?? clock?.epochMsAt?.(currentTime);
    if (Number.isFinite(framePts)) return framePts;
    return null;
  }, [videoRef]);

  /**
   * Temporary: the playhead plus diagnostics for comparing against the OSD.
   */
  const getPlayheadDebug = useCallback(() => {
    const epochMs = getMediaTimestampMs();
    if (!Number.isFinite(epochMs)) return null;
    const clock = videoClockRef.current;
    return {
      epochMs,
      seekOffsetMs: clock?.seekOffsetMs ?? null,
      firstFramePtsMs: clock?.firstFramePtsEpochMs ?? null,
      mediaOffsetMs: Number.isFinite(clock?.mediaOffsetSeconds)
        ? Math.round(clock.mediaOffsetSeconds * 1000)
        : null,
      mediaTime: presentedFrameRef.current?.mediaTime ?? null,
    };
  }, [getMediaTimestampMs]);

  const start = useCallback(
    async ({ startTime, endTime, reconnect = false } = {}) => {
      if (!enabled || !deviceId || !accessToken || !requestCreatePlaybackStream) {
        return false;
      }
      if (!Number.isFinite(Number(startTime)) || !Number.isFinite(Number(endTime))) {
        setStatus("Error: invalid playback time range");
        return false;
      }

      const rangeKey = `${deviceId}:${startTime}-${endTime}`;
      if (activeRangeRef.current === rangeKey && abortRef.current) return true;

      const generation = ++generationRef.current;
      userPausedRef.current = false;
      streamEndedRef.current = false;
      if (!reconnect) reconnectRef.current = { lastResumeMs: null, attempts: 0 };
      const seekRequestedAt = performance.now();
      dbg("[Seek Request]", {
        t: Number(seekRequestedAt.toFixed(1)),
        deviceId,
        targetPtsSec: Number(startTime),
        targetSeekIso: new Date(Number(startTime) * 1000).toISOString(),
        endIso: new Date(Number(endTime) * 1000).toISOString(),
      });
      teardown();
      setAwaitingPicture(true);
      pictureReadyRef.current = false;
      setStatus("playback · command…");

      try {
        const response = await requestCreatePlaybackStream({
          deviceId,
          streamId,
          startTime: Number(startTime),
          endTime: Number(endTime),
          siteId: deviceSiteId,
        });

        if (generation !== generationRef.current) return false;

        const streamInfo = extractPlaybackStreamInfo(response);
        if (!streamInfo) {
          throw new Error("createPlaybackStream missing multipart streamUrl");
        }

        const videoEl = videoRef.current;
        if (!videoEl) throw new Error("playback video element unavailable");
        videoEl.muted = mutedRef.current;
        const stalls = attachVideoStallCounter(videoEl);
        detachVideoDebugRef.current = stalls.detach;

        const abortController = new AbortController();
        abortRef.current = abortController;

        const audioSink = new G711AudioSink(
          () => new (window.AudioContext || window.webkitAudioContext)()
        );
        audioSinkRef.current = audioSink;
        // Never await the unlock: without a prior user gesture the autoplay
        // policy leaves AudioContext.resume() pending indefinitely, which would
        // block the stream from ever being fetched.
        if (!mutedRef.current) {
          Promise.resolve(audioSink.unlock?.()).catch((err) => {
            dbg("audio unlock deferred", { message: err?.message });
          });
        }

        const muxer = new PlaybackVideoMuxer((options = {}) =>
          new JMuxer({
            ...options,
            node: videoEl,
          })
        );
        muxerRef.current = muxer;

        setStatus("playback · fetching stream…");
        const mediaResponse = await fetch(streamInfo.streamUrl, {
          signal: abortController.signal,
        });

        if (generation !== generationRef.current) return false;

        activeRangeRef.current = rangeKey;
        activeStartSecRef.current = Number(startTime);
        const videoClock = new PlaybackVideoClock();
        videoClock.setAnchorEpochMs(Number(startTime) * 1000);
        videoClockRef.current = videoClock;
        // Keep awaitingPicture until the first frame is painted — clearing it
        // here (stream URL ready) made the timeline run ~1s ahead of the OSD
        // on slow NVR sources.
        setStatus("playback · buffering…");
        startStallWatchdog(generation, stalls.counter);

        /**
         * Media-clock pump: playhead = firstFrameActualPts + mediaTime.
         * Base is the absolute PTS from [First Frame Arrived], not targetSeek.
         * Stall → RVFC stops or mediaTime stops advancing → timeline freezes.
         */
        let lastPresentedMediaTime = null;
        let dynamicSample = null;
        let dynamicFrameCount = 0;
        const onPresentedFrame = (mediaTime) => {
          if (generation !== generationRef.current) return;
          // Latch only after the buffer controller has started playback.
          if (videoEl.paused || videoEl.readyState < 2) return;
          const clock = videoClockRef.current;
          // Wait until the first frame header has given us an absolute PTS base.
          if (!Number.isFinite(clock?.firstFramePtsEpochMs)) return;
          // Same composed frame (or decoder freeze): do not push the timeline.
          if (lastPresentedMediaTime !== null
            && Math.abs(mediaTime - lastPresentedMediaTime) < 0.0005) {
            return;
          }
          lastPresentedMediaTime = mediaTime;
          const ptsEpochMs = clock.epochMsAt(mediaTime);
          if (!Number.isFinite(ptsEpochMs)) return;
          presentedFrameRef.current = { mediaTime, ptsEpochMs };
          dynamicFrameCount += 1;

          if (!pictureReadyRef.current) {
            pictureReadyRef.current = true;
            setAwaitingPicture(false);
            dynamicSample = {
              atWall: performance.now(),
              mediaTime,
            };
            dbg("[First Frame Rendered]", {
              t: Number(performance.now().toFixed(1)),
              sinceSeekMs: Math.round(performance.now() - seekRequestedAt),
              mediaTime: Number(mediaTime.toFixed(3)),
              currentTime: Number(videoEl.currentTime.toFixed(3)),
              firstFramePtsIso: new Date(clock.firstFramePtsEpochMs).toISOString(),
              basePtsIso: new Date(ptsEpochMs).toISOString(),
              seekOffsetMs: clock.seekOffsetMs,
              readyState: videoEl.readyState,
            });
            dbg("[Clock Init]", {
              t: Number(performance.now().toFixed(1)),
              firstFramePtsMs: Math.round(clock.firstFramePtsEpochMs),
              firstFramePtsIso: new Date(clock.firstFramePtsEpochMs).toISOString(),
              basePtsMs: Math.round(ptsEpochMs),
              basePtsIso: new Date(ptsEpochMs).toISOString(),
              mediaTimeMs: Math.round(mediaTime * 1000),
              seekAnchorIso: new Date(Number(startTime) * 1000).toISOString(),
              seekOffsetMs: clock.seekOffsetMs,
              formula: "firstFramePts + mediaTime",
            });
            setStatus("playback · playing");
          } else if (dynamicSample && PLAYBACK_DEBUG) {
            // Every ~1s or 30 presented frames: media vs wall advance vs rate.
            const wallMs = performance.now() - dynamicSample.atWall;
            if (wallMs >= 1000 || dynamicFrameCount >= 30) {
              const mediaMs = (mediaTime - dynamicSample.mediaTime) * 1000;
              const rate = videoEl.playbackRate || 1;
              const ratio = wallMs > 0 ? mediaMs / wallMs : null;
              dbg("[Dynamic Tracking]", {
                t: Number(performance.now().toFixed(1)),
                currentPlaybackRate: Number(rate.toFixed(3)),
                mediaTimeDelta: Number((mediaMs / 1000).toFixed(3)),
                wallClockDelta: Number((wallMs / 1000).toFixed(3)),
                ratio: ratio === null ? null : Number(ratio.toFixed(3)),
                // ratio should ≈ currentPlaybackRate if the timeline is media-clocked.
                rateMatch: ratio === null
                  ? null
                  : Math.abs(ratio - rate) < 0.08,
                playheadIso: new Date(ptsEpochMs).toISOString(),
                frames: dynamicFrameCount,
              });
              dynamicSample = { atWall: performance.now(), mediaTime };
              dynamicFrameCount = 0;
            }
          }

          onPlayheadPtsRef.current?.(ptsEpochMs);
        };

        const onWaiting = () => {
          if (generation !== generationRef.current) return;
          logPauseCheck("waiting/buffer");
        };
        videoEl.addEventListener("waiting", onWaiting);

        const armPresentedFramePump = () => {
          cancelFramePumpRef.current?.();
          if (typeof videoEl.requestVideoFrameCallback === "function") {
            const pending = { handle: null };
            const pump = () => {
              pending.handle = videoEl.requestVideoFrameCallback((_now, metadata) => {
                if (generation !== generationRef.current) return;
                const mediaTime = Number.isFinite(metadata?.mediaTime)
                  ? metadata.mediaTime
                  : videoEl.currentTime;
                onPresentedFrame(mediaTime);
                pump();
              });
            };
            pump();
            cancelFramePumpRef.current = () => {
              if (pending.handle !== null) {
                videoEl.cancelVideoFrameCallback?.(pending.handle);
                pending.handle = null;
              }
              videoEl.removeEventListener("waiting", onWaiting);
            };
            return;
          }
          const onTimeUpdate = () => {
            if (videoEl.paused) return;
            onPresentedFrame(videoEl.currentTime);
          };
          videoEl.addEventListener("timeupdate", onTimeUpdate);
          videoEl.addEventListener("playing", onTimeUpdate);
          cancelFramePumpRef.current = () => {
            videoEl.removeEventListener("timeupdate", onTimeUpdate);
            videoEl.removeEventListener("playing", onTimeUpdate);
            videoEl.removeEventListener("waiting", onWaiting);
          };
        };

        armPresentedFramePump();

        // Run multipart consumption without blocking start()'s return —
        // seek/teardown abort the fetch via AbortController.
        playMultipartResponse(mediaResponse, {
          muxer,
          audioSink,
          media: videoEl,
          videoClock,
          signal: abortController.signal,
          // Playback has no volume control: while muted the audio track is
          // dropped, and it may never gate video even if it is turned on
          // before the autoplay policy lets the AudioContext start.
          dropAudio: () => mutedRef.current,
          dropAudioWhenBlocked: true,
          isUserPaused: () => userPausedRef.current,
          // Audio and video parts interleave; only video changes the label so the
          // status line does not flicker between codecs on every part.
          onType: (contentType) => {
            if (generation !== generationRef.current) return;
            if (!contentType.startsWith("video/")) return;
            setStatus(`playback · ${contentType}`);
          },
        })
          .then(async (result) => {
            if (generation !== generationRef.current) return;
            const truncated = result?.truncated === true;
            const markEnded = () => {
              streamEndedRef.current = true;
              setStatus("playback · ended");
              activeRangeRef.current = null;
            };
            if (truncated) setStatus("playback · connection lost, playing buffered…");
            // A clean close can still come early (relay/NVR ended the session
            // well before endTime), so both cases play out and then compare.
            const drained = await waitForPlayoutDrain(videoEl, {
              isCurrent: () => generation === generationRef.current,
              isUserPaused: () => userPausedRef.current,
            });
            if (!drained || generation !== generationRef.current) return;
            // Dropped before the first frame was shown: retry from the original start.
            const resumeMs = presentedFrameRef.current?.ptsEpochMs
              ?? getMediaTimestampMs()
              ?? Number(startTime) * 1000;
            const resumeSec = Number.isFinite(resumeMs) ? Math.floor(resumeMs / 1000) : null;
            if (resumeSec === null || resumeSec >= Number(endTime) - RECONNECT_END_MARGIN_SECONDS) {
              markEnded();
              return;
            }
            const attempt = reconnectRef.current;
            const progressed = attempt.lastResumeMs === null
              || resumeMs - attempt.lastResumeMs >= RECONNECT_MIN_PROGRESS_MS;
            const attempts = progressed ? 1 : attempt.attempts + 1;
            if (attempts > RECONNECT_MAX_ATTEMPTS) {
              // Repeated clean closes at the same spot usually mean no recording
              // there, so only a dropped connection is reported as an error.
              if (!truncated) {
                markEnded();
                return;
              }
              setStatus("Error: playback stream keeps disconnecting");
              teardown();
              return;
            }
            reconnectRef.current = { lastResumeMs: resumeMs, attempts };
            console.warn(
              `[PlaybackReconnect] stream ${truncated ? "dropped" : "ended early"}`
              + ` — resuming at ${new Date(resumeSec * 1000).toISOString()}`
              + ` (attempt ${attempts}/${RECONNECT_MAX_ATTEMPTS})`,
            );
            startRef.current?.({ startTime: resumeSec, endTime: Number(endTime), reconnect: true });
          })
          .catch((error) => {
            if (generation !== generationRef.current) return;
            if (error?.name === "AbortError") return;
            dbg("multipart error", { name: error?.name, message: error?.message });
            setStatus(`Error: ${error.message}`);
            teardown();
          });

        return true;
      } catch (err) {
        if (generation !== generationRef.current) return false;
        if (err?.message === "createPlaybackStream superseded") return false;
        if (err?.name === "AbortError") return false;
        dbg("start error", { message: err?.message });
        setStatus(`Error: ${err.message}`);
        teardown();
        return false;
      }
    },
    [
      enabled,
      deviceId,
      accessToken,
      streamId,
      deviceSiteId,
      requestCreatePlaybackStream,
      teardown,
      setStatus,
      videoRef,
      startStallWatchdog,
      logPauseCheck,
      getMediaTimestampMs,
    ]
  );
  startRef.current = start;

  const stop = useCallback(() => {
    generationRef.current += 1;
    teardown();
    setStatus("idle");
  }, [teardown, setStatus]);

  const pause = useCallback(() => {
    userPausedRef.current = true;
    videoRef.current?.pause?.();
    // Hold (not reset) keeps queued audio, so resume continues in sync.
    audioSinkRef.current?.holdPlayback?.(true);
    logPauseCheck("user-pause");
    setStatus("paused");
  }, [videoRef, setStatus, logPauseCheck]);

  /** Returns false when there is no live stream to resume (caller must restart). */
  const resume = useCallback(() => {
    if (!abortRef.current || streamEndedRef.current) return false;
    userPausedRef.current = false;
    audioSinkRef.current?.holdPlayback?.(false);
    audioSinkRef.current?.unlock?.()?.catch?.(() => null);
    videoRef.current?.play?.().catch(() => {});
    setStatus("playback · playing");
    return true;
  }, [videoRef, setStatus]);

  return {
    status,
    awaitingPicture,
    start,
    pause,
    resume,
    stop,
    getMediaTimestampMs,
    getPlayheadDebug,
  };
}
